/* eslint-disable import/no-extraneous-dependencies */
import React, { useRef, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import Model from '@tripian/model';
import classes from './KiwiHotelSearchBar.scss';

export type KiwiCitySuggestion = {
  id: number;
  name: string;
  parentName: string;
};

interface IKiwiHotelSearchBar {
  destination: string;
  checkInDate: string;
  checkOutDate: string;
  adults: number;
  children: number;
  citySuggestions?: KiwiCitySuggestion[];
  onDestinationChange: (value: string) => void;
  onCitySelect?: (city: KiwiCitySuggestion) => void;
  onCheckInChange: (value: string) => void;
  onCheckOutChange: (value: string) => void;
  onAdultsChange: (value: number) => void;
  onChildrenChange: (value: number) => void;
  onSearch: () => void;
  t: (value: Model.TranslationKey) => string;
}

const formatDate = (dateStr: string): string => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatDateLong = (dateStr: string): string => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatDateRangeShort = (ci: string, co: string): string => {
  if (!ci || !co) return '';
  const d1 = new Date(ci);
  const d2 = new Date(co);
  if (Number.isNaN(d1.getTime()) || Number.isNaN(d2.getTime())) return '';
  const m1 = d1.toLocaleString('en-US', { month: 'short' });
  const m2 = d2.toLocaleString('en-US', { month: 'short' });
  const day1 = d1.getDate();
  const day2 = d2.getDate();
  if (m1 === m2) return `${day1}-${day2} ${m1}`;
  return `${day1} ${m1} - ${day2} ${m2}`;
};

const KiwiHotelSearchBar: React.FC<IKiwiHotelSearchBar> = ({
  destination,
  checkInDate,
  checkOutDate,
  adults,
  children,
  citySuggestions,
  onDestinationChange,
  onCitySelect,
  onCheckInChange,
  onCheckOutChange,
  onAdultsChange,
  onChildrenChange,
  onSearch,
  t,
}) => {
  const checkInRef = useRef<HTMLInputElement>(null);
  const checkOutRef = useRef<HTMLInputElement>(null);
  const destRef = useRef<HTMLDivElement>(null);
  const [guestsOpen, setGuestsOpen] = useState(false);
  const [destOpen, setDestOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileDestOpen, setMobileDestOpen] = useState(false);
  const mobileCheckInRef = useRef<HTMLInputElement>(null);
  const mobileCheckOutRef = useRef<HTMLInputElement>(null);
  const guestsRef = useRef<HTMLDivElement>(null);

  const guestsText = `${adults} ${adults === 1 ? t('kiwi.searchBar.adult') : t('kiwi.common.adults')}${children > 0 ? `, ${children} ${children === 1 ? t('kiwi.searchBar.child') : t('kiwi.common.children')}` : ''}`;
  const totalGuests = adults + children;
  const mobileDateRange = formatDateRangeShort(checkInDate, checkOutDate);
  const mobilePillSubtitle = [mobileDateRange, `${totalGuests} ${totalGuests === 1 ? t('kiwi.searchBar.guest') : t('kiwi.searchBar.guestPlural')}`].filter(Boolean).join(' · ');

  const hasSuggestions = citySuggestions && citySuggestions.length > 0;

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (guestsRef.current && !guestsRef.current.contains(e.target as Node)) {
        setGuestsOpen(false);
      }
      if (destRef.current && !destRef.current.contains(e.target as Node)) {
        setDestOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prevOverflow;
      };
    }
    return undefined;
  }, [mobileOpen]);

  const handleClearAll = () => {
    onDestinationChange('');
    onCheckInChange('');
    onCheckOutChange('');
    onAdultsChange(2);
    onChildrenChange(0);
  };

  const handleMobileSearch = () => {
    setMobileOpen(false);
    onSearch();
  };

  return (
    <>
      <div className={classes.searchBar}>
        {/* Destination */}
        <div className={`${classes.section} ${classes.sectionDestination}`} ref={destRef}>
          <span className={classes.label}>{t('kiwi.searchBar.destination.label')}</span>
          <input
            type="text"
            className={classes.input}
            value={destination}
            onChange={(e) => {
              onDestinationChange(e.target.value);
              setDestOpen(true);
            }}
            onFocus={() => setDestOpen(true)}
            placeholder={t('kiwi.searchBar.searchPlaceholder')}
          />

          {destOpen && hasSuggestions && (
            <div className={classes.suggestionsDropdown}>
              {citySuggestions.map((city) => (
                <div
                  key={city.id}
                  className={classes.suggestionItem}
                  onClick={() => {
                    if (onCitySelect) onCitySelect(city);
                    onDestinationChange(city.name);
                    setDestOpen(false);
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={() => {}}
                >
                  <span className={classes.suggestionName}>{city.name}</span>
                  <span className={classes.suggestionParent}>{city.parentName}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Check-in */}
        <div className={classes.section} onClick={() => checkInRef.current?.showPicker?.()} role="button" tabIndex={0} onKeyDown={() => {}}>
          <span className={classes.label}>{t('kiwi.common.checkIn')}</span>
          <div className={classes.dateDisplay}>
            <span className={checkInDate ? classes.value : `${classes.value} ${classes.valuePlaceholder}`}>{checkInDate ? formatDate(checkInDate) : t('kiwi.searchBar.selectDate')}</span>
            <input ref={checkInRef} type="date" className={classes.hiddenDateInput} value={checkInDate} onChange={(e) => onCheckInChange(e.target.value)} />
          </div>
        </div>

        {/* Check-out */}
        <div className={classes.section} onClick={() => checkOutRef.current?.showPicker?.()} role="button" tabIndex={0} onKeyDown={() => {}}>
          <span className={classes.label}>{t('kiwi.common.checkOut')}</span>
          <div className={classes.dateDisplay}>
            <span className={checkOutDate ? classes.value : `${classes.value} ${classes.valuePlaceholder}`}>{checkOutDate ? formatDate(checkOutDate) : t('kiwi.searchBar.selectDate')}</span>
            <input ref={checkOutRef} type="date" className={classes.hiddenDateInput} value={checkOutDate} onChange={(e) => onCheckOutChange(e.target.value)} />
          </div>
        </div>

        {/* Guests */}
        <div className={classes.section} ref={guestsRef}>
          <span className={classes.label}>{t('kiwi.common.guests')}</span>
          <div className={classes.dateDisplay} onClick={() => setGuestsOpen((prev) => !prev)} role="button" tabIndex={0} onKeyDown={() => {}}>
            <span className={classes.value}>{guestsText}</span>
          </div>

          {guestsOpen && (
            <div className={classes.guestsDropdown}>
              <div className={classes.guestRow}>
                <span className={classes.guestRowLabel}>{t('kiwi.common.adults')}</span>
                <div className={classes.guestCounter}>
                  <button type="button" className={classes.counterBtn} onClick={() => onAdultsChange(Math.max(1, adults - 1))}>−</button>
                  <span className={classes.counterValue}>{adults}</span>
                  <button type="button" className={classes.counterBtn} onClick={() => onAdultsChange(Math.min(9, adults + 1))}>+</button>
                </div>
              </div>
              <div className={classes.guestRow}>
                <span className={classes.guestRowLabel}>{t('kiwi.common.children')}</span>
                <div className={classes.guestCounter}>
                  <button type="button" className={classes.counterBtn} onClick={() => onChildrenChange(Math.max(0, children - 1))}>−</button>
                  <span className={classes.counterValue}>{children}</span>
                  <button type="button" className={classes.counterBtn} onClick={() => onChildrenChange(Math.min(9, children + 1))}>+</button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Search Button */}
        <button type="button" className={classes.searchButton} onClick={onSearch} aria-label={t('kiwi.searchBar.searchHotels')}>
          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
          </svg>
        </button>
      </div>

      {/* ── Mobile collapsed pill ── */}
      <button type="button" className={classes.mobilePill} onClick={() => setMobileOpen(true)} aria-label={t('kiwi.searchBar.openSearch')}>
        <span className={classes.mobilePillIcon}>
          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
            <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
          </svg>
        </span>
        <span className={classes.mobilePillText}>
          <span className={classes.mobilePillTitle}>{destination || t('kiwi.searchBar.destination.placeholder')}</span>
          <span className={classes.mobilePillSubtitle}>{mobilePillSubtitle}</span>
        </span>
      </button>

      {/* ── Mobile full-screen modal (portal to body) ── */}
      {mobileOpen && ReactDOM.createPortal(
        <div className={classes.mobileModal} role="dialog" aria-modal="true">
          <button type="button" className={classes.mobileCloseBtn} onClick={() => setMobileOpen(false)} aria-label={t('kiwi.common.close')}>
            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="currentColor" />
            </svg>
          </button>

          <div className={classes.mobileModalContent}>
            <label className={classes.mobileLabel}>
              {t('kiwi.searchBar.destination.placeholder')}<span className={classes.mobileRequired}>*</span>
            </label>
            <div className={classes.mobileField}>
              <input
                type="text"
                className={classes.mobileInput}
                value={destination}
                onChange={(e) => {
                  onDestinationChange(e.target.value);
                  setMobileDestOpen(true);
                }}
                onFocus={() => setMobileDestOpen(true)}
                placeholder={t('kiwi.searchBar.searchPlaceholder')}
              />
              <svg className={classes.mobileChevron} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z" fill="currentColor" />
              </svg>
            </div>

            {mobileDestOpen && hasSuggestions && (
              <div className={classes.mobileSuggestions}>
                {citySuggestions.map((city) => (
                  <div
                    key={city.id}
                    className={classes.suggestionItem}
                    onClick={() => {
                      if (onCitySelect) onCitySelect(city);
                      onDestinationChange(city.name);
                      setMobileDestOpen(false);
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={() => {}}
                  >
                    <span className={classes.suggestionName}>{city.name}</span>
                    <span className={classes.suggestionParent}>{city.parentName}</span>
                  </div>
                ))}
              </div>
            )}

            <label className={classes.mobileLabel}>{t('kiwi.searchBar.selectDates')}</label>
            <div className={classes.mobileDateRange}>
              <div
                className={classes.mobileDateCell}
                onClick={() => mobileCheckInRef.current?.showPicker?.()}
                role="button"
                tabIndex={0}
                onKeyDown={() => {}}
              >
                <span className={checkInDate ? classes.mobileDateText : `${classes.mobileDateText} ${classes.mobileDatePlaceholder}`}>
                  {checkInDate ? formatDateLong(checkInDate) : t('kiwi.common.checkIn')}
                </span>
                <input
                  ref={mobileCheckInRef}
                  type="date"
                  className={classes.hiddenDateInput}
                  value={checkInDate}
                  onChange={(e) => onCheckInChange(e.target.value)}
                />
              </div>
              <svg className={classes.mobileDateArrow} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M9.29 6.71a1 1 0 000 1.41L13.17 12l-3.88 3.88a1 1 0 101.42 1.41l4.59-4.59a1 1 0 000-1.41L10.71 6.7a1 1 0 00-1.42.01z" fill="currentColor" />
              </svg>
              <div
                className={classes.mobileDateCell}
                onClick={() => mobileCheckOutRef.current?.showPicker?.()}
                role="button"
                tabIndex={0}
                onKeyDown={() => {}}
              >
                <span className={checkOutDate ? classes.mobileDateText : `${classes.mobileDateText} ${classes.mobileDatePlaceholder}`}>
                  {checkOutDate ? formatDateLong(checkOutDate) : t('kiwi.common.checkOut')}
                </span>
                <input
                  ref={mobileCheckOutRef}
                  type="date"
                  className={classes.hiddenDateInput}
                  value={checkOutDate}
                  min={checkInDate || undefined}
                  onChange={(e) => onCheckOutChange(e.target.value)}
                />
              </div>
            </div>

            <label className={classes.mobileLabel}>{t('kiwi.searchBar.travelers')}</label>
            <div className={classes.mobileTravelerRow}>
              <span className={classes.mobileTravelerLabel}>{t('kiwi.common.adults')}</span>
              <div className={classes.mobileTravelerCounter}>
                <button type="button" className={classes.mobileCounterBtn} onClick={() => onAdultsChange(Math.max(1, adults - 1))} aria-label={t('kiwi.searchBar.decreaseAdults')}>−</button>
                <span className={classes.mobileCounterValue}>{adults}</span>
                <button type="button" className={classes.mobileCounterBtn} onClick={() => onAdultsChange(Math.min(9, adults + 1))} aria-label={t('kiwi.searchBar.increaseAdults')}>+</button>
              </div>
            </div>
            <div className={classes.mobileTravelerRow}>
              <span className={classes.mobileTravelerLabel}>{t('kiwi.common.children')}</span>
              <div className={classes.mobileTravelerCounter}>
                <button type="button" className={classes.mobileCounterBtn} onClick={() => onChildrenChange(Math.max(0, children - 1))} aria-label={t('kiwi.searchBar.decreaseChildren')}>−</button>
                <span className={classes.mobileCounterValue}>{children}</span>
                <button type="button" className={classes.mobileCounterBtn} onClick={() => onChildrenChange(Math.min(9, children + 1))} aria-label={t('kiwi.searchBar.increaseChildren')}>+</button>
              </div>
            </div>
          </div>

          <div className={classes.mobileActions}>
            <button type="button" className={classes.mobileClearBtn} onClick={handleClearAll}>{t('kiwi.common.clearAll')}</button>
            <button type="button" className={classes.mobileSearchBtn} onClick={handleMobileSearch}>{t('kiwi.searchBar.search')}</button>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

export default KiwiHotelSearchBar;
